import * as signalR from "@microsoft/signalr";
import { KbDocumentStatus } from "types/knowledgeBase";

export interface KbDocumentStatusUpdate {
  docId: string;
  status: KbDocumentStatus;
  errorMessage?: string;
}

type StatusChangedCallback = (update: KbDocumentStatusUpdate) => void;

class KbStatusHubService {
  private connection: signalR.HubConnection;
  private callbacks: Set<StatusChangedCallback> = new Set();
  private isSubscribed = false;
  private reconnectAttempts = 0;
  private readonly maxReconnectAttempts = 5;
  private readonly reconnectDelay = 3000;

  constructor() {
    const apiUrl = process.env.REACT_APP_BASE_API_URL;
    if (!apiUrl)
      throw new Error("REACT_APP_BASE_API_URL must be set at build time.");
    const baseUrl = apiUrl.replace(/\/api$/, "");

    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(`${baseUrl}/kbStatusHub`, {
        accessTokenFactory: () => localStorage.getItem("accessToken") || "",
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (retryContext) =>
          retryContext.previousRetryCount >= this.maxReconnectAttempts
            ? null
            : this.reconnectDelay,
      })
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    this.connection.serverTimeoutInMilliseconds = 600000;
    this.connection.keepAliveIntervalInMilliseconds = 300000;

    this.connection.on(
      "DocumentStatusChanged",
      (update: KbDocumentStatusUpdate) => {
        this.callbacks.forEach((cb) => cb(update));
      },
    );

    this.connection.onreconnected(async () => {
      this.reconnectAttempts = 0;
      if (this.isSubscribed) {
        await this._invokeHub("Subscribe");
      }
    });

    this.connection.onclose(() => {
      this.isSubscribed = false;
      if (this.reconnectAttempts < this.maxReconnectAttempts) {
        this.reconnectAttempts++;
        setTimeout(() => this.start(), this.reconnectDelay);
      }
    });
  }

  async start(): Promise<void> {
    if (this.connection.state === signalR.HubConnectionState.Disconnected) {
      try {
        await this.connection.start();
        this.reconnectAttempts = 0;
      } catch (err) {
        console.error("KB status hub connection failed:", err);
      }
    }
  }

  async subscribe(): Promise<void> {
    await this.start();
    await this._invokeHub("Subscribe");
    this.isSubscribed = true;
  }

  async unsubscribe(): Promise<void> {
    this.isSubscribed = false;
    await this._invokeHub("Unsubscribe");
  }

  onStatusChanged(callback: StatusChangedCallback): void {
    this.callbacks.add(callback);
  }

  offStatusChanged(callback: StatusChangedCallback): void {
    this.callbacks.delete(callback);
  }

  isConnected(): boolean {
    return this.connection.state === signalR.HubConnectionState.Connected;
  }

  private async _invokeHub(method: string): Promise<void> {
    if (this.connection.state === signalR.HubConnectionState.Connected) {
      try {
        await this.connection.invoke(method);
      } catch (err) {
        console.error(`KB hub invoke ${method} failed:`, err);
      }
    }
  }
}

const kbStatusHubService = new KbStatusHubService();
export default kbStatusHubService;
